import React, { useState, useMemo } from 'react';
import { 
  ShieldAlert, 
  ShieldCheck, 
  ShieldX, 
  RotateCcw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BEHAVIOR_CATEGORIES = [
  {
    id: 'communication',
    label: 'Communication Patterns',
    behaviors: [
      { id: 'late_replies', label: 'Frequently gives late replies', points: 2, level: 'medium' },
      { id: 'vague_answers', label: 'Gives vague or evasive answers about payment or scope', points: 2, level: 'medium' },
      { id: 'avoids_calls', label: 'Avoids calls or meetings about payment', points: 4, level: 'high' },
      { id: 'pay_after', label: 'Uses excuses like "I\'ll pay after..." repeatedly', points: 4, level: 'high' },
    ],
  },
  {
    id: 'payment',
    label: 'Payment History & Requests',
    behaviors: [
      { id: 'avoids_deposit', label: 'Avoids paying deposit', points: 4, level: 'high' },
      { id: 'missed_installment', label: 'Requests installments but misses first payment', points: 3, level: 'medium-high' },
      { id: 'delayed_payments', label: 'History of delayed or partial payments', points: 4, level: 'high' },
      { id: 'no_contract', label: 'Asks for services without signing a contract', points: 4, level: 'high' },
    ],
  },
  {
    id: 'attitude',
    label: 'Attitude & Behaviour',
    behaviors: [
      { id: 'work_before_pay', label: 'Pushes to start work before payment', points: 4, level: 'high' },
      { id: 'on_trust', label: 'Requests services "on trust" repeatedly', points: 3, level: 'medium-high' },
      { id: 'disregards_terms', label: 'Disregards contract terms', points: 3, level: 'medium-high' },
      { id: 'haggling', label: 'Excessive haggling over small fees', points: 2, level: 'medium' },
    ],
  },
  {
    id: 'verification',
    label: 'Verification & Transparency',
    behaviors: [
      { id: 'suspicious_info', label: 'Provides limited or suspicious company info', points: 3, level: 'medium-high' },
      { id: 'no_references', label: 'Refuses to share references or past work', points: 2, level: 'medium' },
      { id: 'personal_email', label: 'Uses personal email instead of company email for business', points: 2, level: 'medium' },
    ],
  },
  {
    id: 'red_flags',
    label: 'Red Flags in Requests',
    behaviors: [
      { id: 'urgent_no_prep', label: 'Frequent "urgent" requests without preparation', points: 2, level: 'medium' },
      { id: 'scope_creep', label: 'Scope changes frequently without agreeing on extra payment', points: 3, level: 'medium-high' },
      { id: 'high_value_low_cred', label: 'Requests high-value services with low credibility', points: 4, level: 'high' },
    ],
  },
  {
    id: 'reputation',
    label: 'Gut Check & Reputation',
    behaviors: [
      { id: 'negative_reviews', label: 'Negative reviews or warnings from peers', points: 4, level: 'high' },
      { id: 'payment_disputes', label: 'Known in industry for payment disputes', points: 4, level: 'high' },
    ],
  },
];

const LEVEL_COLORS = {
  'medium': { bg: 'bg-amber-500/15', border: 'border-amber-500/25', text: 'text-amber-400', dot: 'bg-amber-400' },
  'medium-high': { bg: 'bg-orange-500/15', border: 'border-orange-500/25', text: 'text-orange-400', dot: 'bg-orange-400' },
  'high': { bg: 'bg-red-500/15', border: 'border-red-500/25', text: 'text-red-400', dot: 'bg-red-400' },
};

function getRiskResult(score) {
  if (score === 0) {
    return {
      level: 'none',
      label: 'No Risk Assessed',
      color: 'text-white/40',
      bgColor: 'bg-white/5',
      borderColor: 'border-white/10',
      icon: Info,
      description: 'Select behaviours above to assess your client\'s non-payment risk.',
      actions: [],
    };
  }
  if (score <= 5) {
    return {
      level: 'low',
      label: 'Low Risk',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/25',
      icon: ShieldCheck,
      description: 'This client shows minimal warning signs. Standard terms should be fine.',
      actions: [
        'Accept with standard payment terms',
        'Send clear invoice with due date',
        'Occasional friendly follow-up if payment is late',
      ],
    };
  }
  if (score <= 10) {
    return {
      level: 'medium',
      label: 'Medium Risk',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/25',
      icon: ShieldAlert,
      description: 'This client shows some concerning signs. Take precautions before starting work.',
      actions: [
        'Require 30-50% deposit upfront before any work begins',
        'Use a written agreement or contract',
        'Monitor communication closely for further red flags',
        'Set firm payment deadlines with late fees',
        'Limit scope until initial payment is received',
      ],
    };
  }
  return {
    level: 'high',
    label: 'High Risk',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/25',
    icon: ShieldX,
    description: 'This client shows multiple high-risk behaviours. Proceed with extreme caution or avoid.',
    actions: [
      'Require full payment upfront before starting any work',
      'Use a comprehensive signed contract with penalty clauses',
      'Consider declining the work — the risk may outweigh the reward',
      'If proceeding, limit your exposure and never overdeliver on credit',
      'Document all communication in writing',
      'Use SettledInFull to generate a firm demand email if issues arise',
    ],
  };
}

export default function ClientRiskChecker() {
  const [selected, setSelected] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set(['communication']));

  const toggleBehavior = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (id) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const score = useMemo(() => {
    let total = 0;
    for (const cat of BEHAVIOR_CATEGORIES) {
      for (const b of cat.behaviors) {
        if (selected.has(b.id)) total += b.points;
      }
    }
    return total;
  }, [selected]);

  const result = getRiskResult(score);
  const ResultIcon = result.icon;
  const maxPossible = BEHAVIOR_CATEGORIES.reduce((s, c) => s + c.behaviors.reduce((s2, b) => s2 + b.points, 0), 0);

  const handleReset = () => {
    setSelected(new Set());
  };

  // Breakdown of selected items
  const selectedBreakdown = useMemo(() => {
    const items = [];
    for (const cat of BEHAVIOR_CATEGORIES) {
      for (const b of cat.behaviors) {
        if (selected.has(b.id)) {
          items.push({ ...b, category: cat.label });
        }
      }
    }
    return items;
  }, [selected]);

  return (
    <div data-testid="client-risk-checker">
      {/* Checker Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-clash text-xl font-medium text-white">Client Risk Checker</h3>
          <p className="text-white/50 text-sm mt-1">Select behaviours you've observed to assess non-payment risk</p>
        </div>
        {selected.size > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-white/40 hover:text-white/70"
            data-testid="risk-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            Reset
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Behavior Checklist — Left Side */}
        <div className="lg:col-span-3 space-y-3">
          {BEHAVIOR_CATEGORIES.map((cat) => {
            const isExpanded = expandedCategories.has(cat.id);
            const catSelectedCount = cat.behaviors.filter((b) => selected.has(b.id)).length;

            return (
              <div
                key={cat.id}
                className="bg-black/20 rounded-lg border border-white/6 overflow-hidden"
              >
                <button
                  onClick={() => toggleCategory(cat.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                  data-testid={`risk-category-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-white/80">{cat.label}</span>
                    {catSelectedCount > 0 && (
                      <span className="text-xs bg-[#FF4500]/20 text-[#FF4500] px-2 py-0.5 rounded-full font-medium">
                        {catSelectedCount}
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-white/30" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-white/30" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-3 space-y-1.5">
                    {cat.behaviors.map((b) => {
                      const isChecked = selected.has(b.id);
                      const levelStyle = LEVEL_COLORS[b.level];

                      return (
                        <label
                          key={b.id}
                          data-testid={`risk-behavior-${b.id}`}
                          className={`flex items-start gap-3 p-2.5 rounded-md cursor-pointer transition-all ${
                            isChecked
                              ? `${levelStyle.bg} ${levelStyle.border} border`
                              : 'hover:bg-white/[0.03] border border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleBehavior(b.id)}
                            className="mt-0.5 accent-[#FF4500] w-4 h-4 rounded shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm leading-snug ${isChecked ? 'text-white' : 'text-white/60'}`}>
                              {b.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <div className={`w-1.5 h-1.5 rounded-full ${levelStyle.dot}`} />
                            <span className={`text-xs ${levelStyle.text} capitalize`}>
                              {b.level}
                            </span>
                            <span className="text-xs text-white/20 ml-1">+{b.points}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Result Panel — Right Side */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 space-y-4">
            {/* Score Card */}
            <div className={`rounded-xl border p-5 ${result.bgColor} ${result.borderColor}`} data-testid="risk-score-card">
              <div className="flex items-center gap-3 mb-4">
                <ResultIcon className={`w-7 h-7 ${result.color}`} />
                <div>
                  <p className={`font-clash text-lg font-semibold ${result.color}`} data-testid="risk-score-label">
                    {result.label}
                  </p>
                  <p className="text-white/40 text-xs">
                    Score: {score} / {maxPossible}
                  </p>
                </div>
              </div>

              {/* Score Bar */}
              <div className="w-full h-2 bg-black/30 rounded-full overflow-hidden mb-4">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    score === 0 ? 'bg-white/10' :
                    score <= 5 ? 'bg-emerald-500' :
                    score <= 10 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((score / maxPossible) * 100, 100)}%` }}
                  data-testid="risk-score-bar"
                />
              </div>

              {/* Scale Legend */}
              <div className="flex justify-between text-xs mb-4">
                <span className="text-emerald-400/60">0-5 Low</span>
                <span className="text-amber-400/60">6-10 Medium</span>
                <span className="text-red-400/60">11+ High</span>
              </div>

              <p className="text-white/60 text-sm leading-relaxed">{result.description}</p>
            </div>

            {/* Suggested Actions */}
            {result.actions.length > 0 && (
              <div className="bg-black/20 rounded-xl border border-white/6 p-5" data-testid="risk-actions">
                <h4 className="font-medium text-white text-sm mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#FF4500]" />
                  Suggested Actions
                </h4>
                <ul className="space-y-2.5">
                  {result.actions.map((action, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                        result.level === 'low' ? 'bg-emerald-400' :
                        result.level === 'medium' ? 'bg-amber-400' : 'bg-red-400'
                      }`} />
                      <span className="text-white/70 leading-relaxed">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Breakdown */}
            {selectedBreakdown.length > 0 && (
              <div className="bg-black/20 rounded-xl border border-white/6 p-5" data-testid="risk-breakdown">
                <h4 className="font-medium text-white text-sm mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  Risk Breakdown
                </h4>
                <div className="space-y-2">
                  {selectedBreakdown.map((item) => {
                    const levelStyle = LEVEL_COLORS[item.level];
                    return (
                      <div key={item.id} className="flex items-center justify-between text-xs">
                        <span className="text-white/50 truncate mr-2">{item.label}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className={levelStyle.text}>+{item.points}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-t border-white/8 pt-2 mt-2 flex justify-between font-medium">
                    <span className="text-white/60 text-xs">Total</span>
                    <span className={`text-sm ${result.color}`}>{score} points</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
